import { useEffect } from "react";
import { X, ZoomIn, ZoomOut, RotateCcw, Download } from "lucide-react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

// Universal full-screen image viewer with true two-finger pinch zoom (iOS/Android),
// double-tap zoom, mouse wheel zoom, drag-to-pan, and download/save.
export const ImageZoomViewer = ({ src, alt = "", open, onClose, testid = "image-viewer" }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !src) return null;

  // Decode a data: URL into a File synchronously (no await), so iOS keeps the tap's
  // user-activation and the native Share Sheet ("Save Image" → Photos) actually opens.
  const dataUrlToFile = (dataUrl, filename) => {
    const [head, b64] = dataUrl.split(",");
    const mime = (head.match(/data:(.*?);base64/) || [])[1] || "image/jpeg";
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
    return new File([arr], filename, { type: mime });
  };

  // Save the image to the device. On iOS (incl. installed PWA) an <a download> just
  // flashes and never reaches Photos, so prefer the Web Share Sheet with the file.
  const saveImage = async () => {
    const filename = `${(alt || "tour-sheet").replace(/\s+/g, "-").toLowerCase()}.jpg`;
    try {
      let file;
      if (src.startsWith("data:")) {
        file = dataUrlToFile(src, filename);
      } else {
        const blob = await (await fetch(src)).blob();
        file = new File([blob], filename, { type: blob.type || "image/jpeg" });
      }
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: alt || "Tour Sheet" });
        return;
      }
      // Desktop / unsupported: fall back to a real download.
      const href = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (e) {
      if (e && (e.name === "AbortError" || e.name === "NotAllowedError")) return; // user cancelled
      try { window.open(src, "_blank"); } catch { /* noop */ }
    }
  };

  const btn = "flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20";

  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-black/95 backdrop-blur-sm" data-testid={testid}>
      <TransformWrapper
        initialScale={1}
        minScale={1}
        maxScale={6}
        centerOnInit
        doubleClick={{ mode: "toggle", step: 1.6 }}
        pinch={{ step: 8 }}
        wheel={{ step: 0.2 }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="truncate text-xs font-600 uppercase tracking-wide text-white/60">{alt}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => zoomOut()} className={btn} data-testid={`${testid}-zoom-out`} aria-label="Zoom out"><ZoomOut className="h-4 w-4" /></button>
                <button onClick={() => zoomIn()} className={btn} data-testid={`${testid}-zoom-in`} aria-label="Zoom in"><ZoomIn className="h-4 w-4" /></button>
                <button onClick={() => resetTransform()} className={btn} data-testid={`${testid}-reset`} aria-label="Reset"><RotateCcw className="h-4 w-4" /></button>
                <button onClick={saveImage} className={btn} data-testid={`${testid}-download`} aria-label="Save image"><Download className="h-4 w-4" /></button>
                <button onClick={onClose} className={btn} data-testid={`${testid}-close`} aria-label="Close"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%", flex: 1 }}
              contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <img
                src={src}
                alt={alt}
                draggable={false}
                className="max-h-[80vh] max-w-full select-none object-contain"
                data-testid={`${testid}-img`}
              />
            </TransformComponent>
            <p className="pb-5 pt-2 text-center text-xs text-white/50">Pinch or double-tap to zoom · drag to pan · Esc/✕ to close</p>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};
